import { useState, useEffect, useCallback } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { apiFetch, apiJson } from './api';
import Icon from './Icons';
import ReviewQueueShell from './ReviewQueueShell';
import GuidanceSheet from './GuidanceSheet';

function fieldLabel(path) {
  return path.split('.').map(s => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(' — ');
}

// When a primary field is flagged, also show its stages companion for editing.
const STAGES_COMPANIONS = {
  'leaf.shape':          'leaf.shape_stages',
  'leaf.texture':        'leaf.texture_stages',
  'leaf.venation_color': 'leaf.venation_color_stages',
};

function profileDisplayName(profile) {
  const { genus, species, trade_name } = profile;
  if (trade_name) return `${genus} '${trade_name}'`;
  if (!species) return genus;
  const sp = species.startsWith('sp. ') ? species.slice(4) : species;
  return `${genus} ${sp}`;
}

// ─── Inline chip editor for a single trait field ───────────────────────────

function GlossaryHint({ glossary, hoveredValue }) {
  if (!hoveredValue || !glossary[hoveredValue]) return (
    <div style={{ minHeight: '2.4rem', marginTop: '0.4rem' }}>
      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
        Hover a chip to see its definition
      </span>
    </div>
  );
  const entry = glossary[hoveredValue];
  return (
    <div style={{
      marginTop: '0.4rem',
      padding: '0.45rem 0.6rem',
      background: 'var(--surface-raised, var(--surface))',
      border: '1px solid var(--border)',
      borderRadius: 6,
      fontSize: '0.76rem',
      lineHeight: 1.45,
    }}>
      <strong style={{ color: 'var(--text)' }}>{hoveredValue.replace(/_/g, ' ')}</strong>
      {entry.plain_language && <> — <span style={{ color: 'var(--text)' }}>{entry.plain_language}</span></>}
      {entry.example_plant && (
        <div style={{ marginTop: '0.15rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          e.g. {entry.example_plant}
        </div>
      )}
    </div>
  );
}

function TraitFieldEditor({ field, value, onChange }) {
  const [valueGlossary, setValueGlossary] = useState({});
  const [hoveredValue, setHoveredValue] = useState(null);

  useEffect(() => {
    if (!field?.path) return;
    setHoveredValue(null);
    // stage_pair fields (leaf.texture_stages) share values with their parent field (leaf.texture)
    const glossaryPath = field.path.replace(/_stages$/, '');
    apiJson(`/id-traits/glossary/values/${encodeURIComponent(glossaryPath)}`)
      .then(d => setValueGlossary(d.values || {}))
      .catch(() => setValueGlossary({}));
  }, [field?.path]);

  const hasGlossary = Object.keys(valueGlossary).length > 0;

  if (!field) return null;

  if (field.type === 'array') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>
        <div className="health-chip-row" style={{ flexWrap: 'wrap' }}>
          {field.valid_values.map(v => {
            const active = arr.includes(v);
            return (
              <button
                key={v}
                type="button"
                className={`health-chip ${active ? 'health-chip--active' : ''}`}
                onClick={() => onChange(active ? arr.filter(x => x !== v) : [...arr, v])}
                onMouseEnter={() => setHoveredValue(v)}
                onMouseLeave={() => setHoveredValue(null)}
              >
                {v.replace(/_/g, ' ')}
              </button>
            );
          })}
        </div>
        {hasGlossary && <GlossaryHint glossary={valueGlossary} hoveredValue={hoveredValue} />}
      </div>
    );
  }

  if (field.type === 'string') {
    const cur = value || null;
    return (
      <div>
        <div className="health-chip-row" style={{ flexWrap: 'wrap' }}>
          {field.valid_values.map(v => (
            <button
              key={v}
              type="button"
              className={`health-chip ${cur === v ? 'health-chip--active' : ''}`}
              onClick={() => onChange(cur === v ? null : v)}
              onMouseEnter={() => setHoveredValue(v)}
              onMouseLeave={() => setHoveredValue(null)}
            >
              {v.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
        {hasGlossary && <GlossaryHint glossary={valueGlossary} hoveredValue={hoveredValue} />}
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <div className="health-chip-row">
        {[true, false].map(v => (
          <button
            key={String(v)}
            type="button"
            className={`health-chip ${value === v ? 'health-chip--active' : ''}`}
            onClick={() => onChange(value === v ? null : v)}
          >
            {v ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
    );
  }

  if (field.type === 'stage_pair') {
    const cur = value || { juvenile: [], mature: [] };
    const toggle = (stage, v) => {
      const arr = cur[stage] || [];
      onChange({
        ...cur,
        [stage]: arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v],
      });
    };
    return (
      <div>
        {['juvenile', 'mature'].map(stage => (
          <div key={stage} style={{ marginBottom: '0.4rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.2rem', textTransform: 'capitalize' }}>
              {stage}
            </div>
            <div className="health-chip-row" style={{ flexWrap: 'wrap' }}>
              {field.valid_values.map(v => {
                const active = (cur[stage] || []).includes(v);
                return (
                  <button
                    key={v}
                    type="button"
                    className={`health-chip ${active ? 'health-chip--active' : ''}`}
                    onClick={() => toggle(stage, v)}
                    onMouseEnter={() => setHoveredValue(v)}
                    onMouseLeave={() => setHoveredValue(null)}
                  >
                    {v.replace(/_/g, ' ')}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {hasGlossary && <GlossaryHint glossary={valueGlossary} hoveredValue={hoveredValue} />}
      </div>
    );
  }

  return null;
}

// ─── Read-only chip display ────────────────────────────────────────────────

function TraitValueDisplay({ field, traits }) {
  if (!field || !traits) return <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>—</span>;

  const [group, key] = field.path.split('.');
  const groupData = traits[group] || {};
  const val = groupData[key];

  if (val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) {
    return <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>not set</span>;
  }

  const chipStyle = { fontSize: '0.75rem', background: 'var(--surface-recessed)', border: '1px solid var(--border)', borderRadius: 4, padding: '0.1rem 0.45rem' };

  if (field.type === 'boolean') {
    return (
      <span style={{ display: 'flex', gap: '0.3rem' }}>
        <span style={chipStyle}>{val === true ? 'Yes' : 'No'}</span>
      </span>
    );
  }

  if (field.type === 'stage_pair') {
    const stages = val || {};
    return (
      <span style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        {['juvenile', 'mature'].map(stage => {
          const vals = stages[stage] || [];
          return vals.length > 0 ? (
            <span key={stage} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'capitalize', minWidth: '4rem' }}>{stage}:</span>
              {vals.map(v => <span key={v} style={chipStyle}>{String(v).replace(/_/g, ' ')}</span>)}
            </span>
          ) : null;
        })}
      </span>
    );
  }

  const arr = Array.isArray(val) ? val : [val];
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
      {arr.map(v => (
        <span key={v} style={chipStyle}>
          {String(v).replace(/_/g, ' ')}
        </span>
      ))}
    </span>
  );
}

// ─── Per-profile review card ───────────────────────────────────────────────

function TraitReviewCard({ profile, goNext, schemaByPath, onFlagsChanged }) {
  const [localFlags, setLocalFlags] = useState(() => ({ ...profile.flag_reasons }));
  const [localTraits, setLocalTraits] = useState(() => profile.identification_traits);
  const [editingField, setEditingField] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [glossarySheet, setGlossarySheet] = useState(null); // { title, values: {} }
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [refPhotos, setRefPhotos] = useState([]);
  const [refLoading, setRefLoading] = useState(false);

  // Reset local state when profile changes
  useEffect(() => {
    setLocalFlags({ ...profile.flag_reasons });
    setLocalTraits(profile.identification_traits);
    setEditingField(null);
    setEditValues({});
    setError(null);
    setSaving(null);
  }, [profile.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load reference photos
  useEffect(() => {
    if (!profile.has_reference_photos) return;
    setRefLoading(true);
    apiJson(`/operator/care-profiles/${profile.id}/reference-photos`)
      .then(d => setRefPhotos(d.assigned || []))
      .catch(() => setRefPhotos([]))
      .finally(() => setRefLoading(false));
  }, [profile.id, profile.has_reference_photos]);

  const flagKeys = Object.keys(localFlags);
  const hasFlags = flagKeys.length > 0;

  const advanceIfDone = useCallback((remainingFlags) => {
    if (Object.keys(remainingFlags).length === 0) {
      goNext();
    }
  }, [goNext]);

  async function acceptFlag(path) {
    setSaving(path);
    setError(null);
    try {
      const url = `/operator/care-profiles/${profile.id}/traits/flags?key=${encodeURIComponent(path)}`;
      const res = await apiFetch(url, { method: 'DELETE' });
      if (!res.ok) { setError('Accept failed'); return; }
      // Strip accepted key from localTraits._review_flags so later saveEdit
      // calls don't re-send it to the backend.
      setLocalTraits(prev => {
        if (!prev._review_flags || !(path in prev._review_flags)) return prev;
        const next = { ...prev, _review_flags: { ...prev._review_flags } };
        delete next._review_flags[path];
        if (Object.keys(next._review_flags).length === 0) delete next._review_flags;
        return next;
      });
      setLocalFlags(prev => {
        const next = { ...prev };
        delete next[path];
        onFlagsChanged?.(profile.id, next);
        advanceIfDone(next);
        return next;
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  }

  function startEdit(path) {
    // Flag keys may be sub-paths (e.g. leaf.texture_stages.juvenile); normalize
    // to the 2-segment schema path for field lookup and trait reads.
    const schemaParts = path.split('.');
    const schemaPath = schemaParts.slice(0, 2).join('.');
    const field = schemaByPath[schemaPath];
    if (!field) return;
    const [group, key] = schemaParts;
    const cur = (localTraits[group] || {})[key];
    let initVal;
    if (field.type === 'array') initVal = Array.isArray(cur) ? [...cur] : [];
    else if (field.type === 'stage_pair') initVal = cur ? { ...cur } : { juvenile: [], mature: [] };
    else if (field.type === 'boolean') initVal = cur === true ? true : cur === false ? false : null;
    else initVal = cur || null;
    setEditValues(prev => ({ ...prev, [path]: initVal }));
    setEditingField(path);
  }

  async function saveEdit(path) {
    setSaving(path);
    setError(null);
    try {
      const schemaParts = path.split('.');
      const field = schemaByPath[schemaParts.slice(0, 2).join('.')];
      const [group, key] = schemaParts;

      // Build updated traits — preserve existing, update the one field
      const updatedTraits = JSON.parse(JSON.stringify(localTraits));
      updatedTraits[group] = updatedTraits[group] || {};
      const val = editValues[path];

      if (field.type === 'boolean') {
        if (val !== null && val !== undefined) updatedTraits[group][key] = val;
        else delete updatedTraits[group][key];
      } else if (field.type === 'array') {
        if (Array.isArray(val) && val.length > 0) updatedTraits[group][key] = val;
        else delete updatedTraits[group][key];
      } else if (field.type === 'stage_pair') {
        if (val && ((val.juvenile && val.juvenile.length) || (val.mature && val.mature.length))) {
          updatedTraits[group][key] = val;
        } else {
          delete updatedTraits[group][key];
        }
      } else {
        if (val) updatedTraits[group][key] = val;
        else delete updatedTraits[group][key];
      }

      const res = await apiFetch(`/operator/care-profiles/${profile.id}/traits`, {
        method: 'PATCH',
        body: JSON.stringify({ identification_traits: updatedTraits }),
      });
      if (!res.ok) { setError('Save failed'); return; }

      // Backend auto-strips _review_flags entry when value is valid+non-empty;
      // mirror that locally
      const isNonEmpty = field.type === 'boolean' ? (val !== null && val !== undefined)
        : field.type === 'array' ? (Array.isArray(val) && val.length > 0)
        : field.type === 'stage_pair' ? !!(val && ((val.juvenile && val.juvenile.length) || (val.mature && val.mature.length)))
        : !!val;

      setLocalTraits(updatedTraits);
      setEditingField(null);

      if (isNonEmpty) {
        setLocalFlags(prev => {
          const next = { ...prev };
          delete next[path];
          onFlagsChanged?.(profile.id, next);
          advanceIfDone(next);
          return next;
        });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  }

  async function markAllReviewed() {
    setSaving('__all__');
    setError(null);
    try {
      const res = await apiFetch(`/operator/care-profiles/${profile.id}/traits/flags`, { method: 'DELETE' });
      if (!res.ok) { setError('Failed'); return; }
      setLocalFlags({});
      setLocalTraits(prev => {
        if (!prev._review_flags) return prev;
        const next = { ...prev };
        delete next._review_flags;
        return next;
      });
      onFlagsChanged?.(profile.id, {});
      goNext();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  }

  const searchBase = `${profile.genus} ${profile.species !== 'sp.' ? profile.species : ''}`.trim();

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr min(320px, 35%)', gap: '1.5rem', padding: '1.25rem 1rem', alignItems: 'start' }}>
      {/* ── Main column ── */}
      <div>
        {/* Profile header */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
            <span style={{ fontWeight: 700, fontSize: '1rem' }}>
              <em>{profileDisplayName(profile)}</em>
            </span>
            {profile.profile_type && profile.profile_type !== 'species' && (
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                {profile.profile_type.replace(/_/g, ' ')}
              </span>
            )}
            <span style={{ fontSize: '0.72rem', color: hasFlags ? 'var(--warning, #d97706)' : 'var(--text-muted)' }}>
              {Object.keys(localFlags).length} flag{Object.keys(localFlags).length !== 1 ? 's' : ''}
            </span>
          </div>
          <div>
            <Link
              to={`/admin/care-profiles?profile=${profile.id}&status=approved`}
              style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}
            >
              View in care profiles ↗
            </Link>
          </div>
        </div>

        {/* No flags remaining */}
        {!hasFlags && (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>All flags resolved.</p>
        )}

        {/* Flagged fields */}
        {flagKeys.map(path => {
          // Sub-path flag keys (e.g. leaf.texture_stages.juvenile) normalize to 2-segment schema path.
          const schemaPath = path.split('.').slice(0, 2).join('.');
          const field = schemaByPath[schemaPath];
          const isEditing = editingField === path;
          const isSaving = saving === path;

          const companionPath = STAGES_COMPANIONS[schemaPath];
          const companionField = companionPath ? schemaByPath[companionPath] : null;
          const isCompanionEditing = editingField === companionPath;
          const isCompanionSaving = saving === companionPath;

          return (
            <div key={path} style={{ marginBottom: '1.25rem', padding: '0.85rem', border: '1px solid var(--warning-border, #fcd34d)', borderRadius: 8, background: 'var(--surface)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  {field ? field.label : fieldLabel(path)}
                  {field?.is_cultivar_discriminator && (
                    <span title="Key cultivar discriminator" style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>★</span>
                  )}
                  {field && field.type !== 'boolean' && (
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', padding: '0 0.1rem', cursor: 'pointer', fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1 }}
                      title="View value definitions"
                      onClick={() => {
                        apiJson(`/id-traits/glossary/values/${encodeURIComponent(schemaPath.replace(/_stages$/, ''))}`)
                          .then(d => setGlossarySheet({ title: field.label, values: d.values || {} }))
                          .catch(() => {});
                      }}
                    >?</button>
                  )}
                </span>
                <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                  <button
                    type="button"
                    className="settings-lookup-action"
                    style={{ fontSize: '0.75rem' }}
                    disabled={isSaving || saving === '__all__'}
                    onClick={() => isEditing ? setEditingField(null) : startEdit(path)}
                  >
                    {isEditing ? 'Cancel' : 'Edit'}
                  </button>
                  <button
                    type="button"
                    className="settings-lookup-action"
                    style={{ fontSize: '0.75rem' }}
                    disabled={isSaving || saving === '__all__' || isEditing}
                    onClick={() => acceptFlag(path)}
                  >
                    {isSaving ? 'Accepting…' : 'Accept'}
                  </button>
                </div>
              </div>

              {/* Flag reason */}
              <p style={{ fontSize: '0.76rem', color: 'var(--warning, #d97706)', margin: '0 0 0.5rem', lineHeight: 1.45 }}>
                ⚑ {localFlags[path]}
              </p>

              {/* Current value */}
              {!isEditing && (
                <div style={{ marginBottom: '0.1rem' }}>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginRight: '0.4rem' }}>Current:</span>
                  <TraitValueDisplay field={field} traits={localTraits} />
                </div>
              )}

              {/* Inline editor */}
              {isEditing && field && (
                <div>
                  <TraitFieldEditor
                    field={field}
                    value={editValues[path]}
                    onChange={v => setEditValues(prev => ({ ...prev, [path]: v }))}
                  />
                  <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.4rem' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ fontSize: '0.8rem', padding: '0.35rem 0.9rem' }}
                      disabled={isSaving}
                      onClick={() => saveEdit(path)}
                    >
                      {isSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="settings-lookup-action"
                      style={{ fontSize: '0.8rem' }}
                      onClick={() => setEditingField(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Companion stages field */}
              {companionField && (
                <>
                  <div style={{ margin: '0.75rem 0 0.6rem', borderTop: '1px solid var(--border)', paddingTop: '0.6rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)' }}>
                      {companionField.label}
                    </span>
                    <button
                      type="button"
                      className="settings-lookup-action"
                      style={{ fontSize: '0.75rem', flexShrink: 0 }}
                      disabled={isCompanionSaving || saving === '__all__'}
                      onClick={() => isCompanionEditing ? setEditingField(null) : startEdit(companionPath)}
                    >
                      {isCompanionEditing ? 'Cancel' : 'Edit'}
                    </button>
                  </div>
                  {!isCompanionEditing && (
                    <div>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginRight: '0.4rem' }}>Current:</span>
                      <TraitValueDisplay field={companionField} traits={localTraits} />
                    </div>
                  )}
                  {isCompanionEditing && (
                    <div>
                      <TraitFieldEditor
                        field={companionField}
                        value={editValues[companionPath]}
                        onChange={v => setEditValues(prev => ({ ...prev, [companionPath]: v }))}
                      />
                      <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.4rem' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ fontSize: '0.8rem', padding: '0.35rem 0.9rem' }}
                          disabled={isCompanionSaving}
                          onClick={() => saveEdit(companionPath)}
                        >
                          {isCompanionSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="settings-lookup-action"
                          style={{ fontSize: '0.8rem' }}
                          onClick={() => setEditingField(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}

        {error && (
          <p style={{ fontSize: '0.8rem', color: 'var(--danger)', marginTop: '0.5rem' }}>{error}</p>
        )}

        {/* Mark all reviewed */}
        {hasFlags && (
          <button
            type="button"
            className="settings-lookup-action"
            style={{ marginTop: '0.25rem', fontSize: '0.82rem' }}
            disabled={saving === '__all__' || saving !== null}
            onClick={markAllReviewed}
          >
            {saving === '__all__' ? 'Marking…' : 'Mark all reviewed'}
          </button>
        )}
      </div>

      {/* ── Photo sidebar ── */}
      <div>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.6rem' }}>
          Reference Photos
        </div>

        {refLoading && <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Loading…</p>}

        {!refLoading && refPhotos.length === 0 && (
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: '0.75rem' }}>No reference photos</p>
        )}

        {refPhotos.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
            {refPhotos.map(p => (
              <img
                key={p.reference_photo_id}
                src={`/reference-photos/${p.reference_photo_id}/file${p.transform_version ? `?v=${p.transform_version}` : ''}`}
                alt={p.photo_part || 'Reference'}
                style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }}
              />
            ))}
          </div>
        )}

        {/* Per-flag image search links */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {flagKeys.map(path => {
            const field = schemaByPath[path];
            const label = field ? field.label : fieldLabel(path);
            const q = encodeURIComponent(`${searchBase} ${label}`);
            return (
              <a
                key={path}
                href={`https://www.google.com/search?q=${q}&tbm=isch`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.76rem', color: 'var(--link, var(--primary))', textDecoration: 'none' }}
              >
                Search: {label} ↗
              </a>
            );
          })}
          <a
            href={`https://www.google.com/search?q=${encodeURIComponent(searchBase)}&tbm=isch`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '0.76rem', color: 'var(--text-muted)', textDecoration: 'none', marginTop: '0.15rem' }}
          >
            Search all images ↗
          </a>
        </div>
      </div>

      {/* Glossary sheet */}
      {glossarySheet && (
        <GuidanceSheet
          isOpen={Boolean(glossarySheet)}
          onDismiss={() => setGlossarySheet(null)}
          title={glossarySheet.title}
          variant="sheet"
        >
          {Object.keys(glossarySheet.values).length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.88rem' }}>
              No definitions available yet.
            </p>
          ) : (
            <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {Object.entries(glossarySheet.values).map(([val, entry]) => (
                <div key={val}>
                  <dt style={{ fontWeight: 600, fontSize: '0.88rem' }}>{val.replace(/_/g, ' ')}</dt>
                  {entry.plain_language && (
                    <dd style={{ margin: '0.1rem 0 0', fontSize: '0.84rem', color: 'var(--text)' }}>{entry.plain_language}</dd>
                  )}
                  {entry.botanical_note && (
                    <dd style={{ margin: '0.1rem 0 0', fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>{entry.botanical_note}</dd>
                  )}
                  {entry.example_plant && (
                    <dd style={{ margin: '0.1rem 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>e.g. {entry.example_plant}</dd>
                  )}
                </div>
              ))}
            </dl>
          )}
        </GuidanceSheet>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TraitReview({ session }) {
  const navigate = useNavigate();
  const isAdmin = session?.user?.is_superadmin;

  const [queue, setQueue] = useState([]);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([
      apiJson('/operator/trait-review-queue'),
      apiJson('/id-traits/schema'),
    ])
      .then(([queueData, schemaData]) => {
        setQueue(queueData.queue);
        setSchema(schemaData.fields);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, [isAdmin]);

  const handleFlagsChanged = useCallback((profileId, newFlags) => {
    setQueue(prev => prev.map(item => {
      if (item.id !== profileId) return item;
      const updatedTraits = { ...item.identification_traits };
      if (Object.keys(newFlags).length > 0) {
        updatedTraits._review_flags = newFlags;
      } else {
        delete updatedTraits._review_flags;
      }
      return {
        ...item,
        flag_reasons: newFlags,
        flag_keys: Object.keys(newFlags),
        flag_count: Object.keys(newFlags).length,
        identification_traits: updatedTraits,
      };
    }));
  }, []);

  if (!isAdmin) return <Navigate to="/" replace />;

  const schemaByPath = schema ? Object.fromEntries(schema.map(f => [f.path, f])) : {};

  const header = (
    <header className="sub-header">
      <button
        type="button"
        className="sub-header-back"
        onClick={() => navigate('/admin/care-profiles', { replace: false })}
        aria-label="Back"
        title="Back"
      >
        <Icon name="arrowLeft" size={20} />
      </button>
      <div className="sub-header-title"><h1>Trait Review</h1></div>
    </header>
  );

  if (loading) {
    return (
      <div className="app-scroll-area">
        {header}
        <div className="stats-content">
          <p className="health-loading">Loading trait review queue…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-scroll-area">
        {header}
        <div className="stats-content">
          <p style={{ color: 'var(--danger)', fontSize: '0.9rem' }}>{error}</p>
        </div>
      </div>
    );
  }

  const emptyNode = (
    <div className="app-scroll-area">
      {header}
      <div className="stats-content" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
        <p style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.35rem' }}>All trait flags resolved</p>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>No flagged profiles remaining in the queue.</p>
      </div>
    </div>
  );

  if (done || queue.length === 0) return emptyNode;

  return (
    <div className="app-scroll-area">
      {header}
      <ReviewQueueShell
        items={queue}
        label="Trait Review"
        onComplete={() => setDone(true)}
        emptyState={emptyNode}
        renderItem={(profile, { goNext }) => (
          <TraitReviewCard
            key={profile.id}
            profile={profile}
            goNext={goNext}
            schemaByPath={schemaByPath}
            onFlagsChanged={handleFlagsChanged}
          />
        )}
      />
    </div>
  );
}
